import { Data, Image, ImageSource } from "./types";
import { PEXELS_KEY, PIXABAY_KEY, UNSPLASH_KEY } from "./apiKeys";

export const officialCollection = 1053828;

type Config = Pick<
  Data,
  "source" | "by" | "collections" | "featured" | "search" | "topics"
>;

// ---- Unsplash ----

const dailySeed = Math.floor(Date.now() / 86400000);

const UNSPLASH_QUERIES = [
  "serene mountain sunrise",
  "misty morning forest",
  "golden hour ocean waves",
  "peaceful lake reflection",
  "sun rays through trees",
  "wildflower meadow spring",
  "tranquil countryside",
  "foggy valley dawn",
  "ethereal light nature",
  "lush green hills",
  "still water sunset",
  "snowy peak alpine glow",
  "starry night sky",
  "the Milky Way",
];
const PEXELS_QUERIES = [
  "rolling green hills",
  "ocean waves sunset",
  "majestic mountain peak",
  "soft morning light meadow",
  "tranquil lake sunrise",
  "peaceful forest path",
  "golden hour nature",
  "serene mountain landscape",
  "snow capped mountains",
  "riverside autumn colors",
  "starry night sky",
  "the Milky Way",
];
const PIXABAY_QUERIES = [
  "the Milky Way",
  "green meadow hills",
  "starry night sky",
  "sunrise over ocean",
  "wildflower field spring",
  "peaceful river valley",
  "misty forest morning",
  "autumn forest sunlight",
  "mountain lake reflection",
  "waterfall forest stream",
  "serene nature landscape",
];

const pickQuery = (queries: string[]) => queries[dailySeed % queries.length];

const fetchUnsplash = async (config: Config): Promise<Image[]> => {
  const url = "https://api.unsplash.com/photos/random";
  const params = new URLSearchParams();
  const headers = new Headers({
    Authorization: `Client-ID ${UNSPLASH_KEY}`,
  });

  params.set("count", "30");

  switch (config.by) {
    case "collections":
      params.set("collections", config.collections);
      break;
    case "topics":
      params.set("topics", config.topics);
      params.set("orientation", "landscape");
      break;
    case "search":
      params.set("orientation", "landscape");
      params.set("featured", "true");
      if (config.search) {
        params.set("query", config.search);
      } else {
        params.set("query", pickQuery(UNSPLASH_QUERIES));
      }
      break;
    default:
      params.set("collections", String(officialCollection));
  }

  const res = await fetch(`${url}?${params}`, { headers, cache: "no-cache" });
  const body = await res.json();

  const blacklist = /animal|bird|dog|cat|pet|horse|deer|bear|wolf|fox|lion|tiger|elephant|cow|sheep|goat|pig|rabbit|squirrel|monkey|ape|fish|whale|shark|dolphin|octopus|jellyfish|reptile|insect|snake|spider|bug|worm|butterfly|eagle|hawk|owl|people|face|person|portrait|selfie|man|woman|child|kid|baby|family|crowd|group|posing|sitting|standing|walking|running|jumping|dancing|smiling|looking|wearing|dress|shirt|hat|glasses/i;
  const filtered = body.filter((item: any) => {
    const desc = (item.description || "").toLowerCase();
    const alt = (item.alt_description || "").toLowerCase();
    const tags = (item.tags || []).map((t: any) => (t.title || "").toLowerCase()).join(" ");
    return !blacklist.test(desc + " " + alt + " " + tags);
  });

  return filtered.map((item: any) => ({
    src: item.urls.raw,
    source: "Unsplash",
    sourceLink: "https://unsplash.com",
    credit: {
      imageLink: item.links.html,
      location: item.location?.name ?? undefined,
      userName: item.user.name,
      userLink: item.user.links.html,
    },
  }));
};

// ---- Pexels ----

const fetchPexels = async (config: Config): Promise<Image[]> => {
  const query = config.search || pickQuery(PEXELS_QUERIES);
  const url = `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}&per_page=40&orientation=landscape&size=large`;
  const headers = new Headers({
    Authorization: PEXELS_KEY,
  });

  const res = await fetch(url, { headers, cache: "no-cache" });
  const body = await res.json();
  console.log("[Nature Wallpaper] Pexels response:", res.status, "photos:", body.photos?.length, "total:", body.total_results);

  if (!body.photos) return [];

  const blacklist = /people|face|logo|brand|building|architecture|text|city|street|car|vehicle|animal|bird|dog|cat|pet|horse|deer|bear|wolf|fox|lion|tiger|elephant|cow|sheep|goat|pig|rabbit|squirrel|monkey|ape|fish|whale|shark|dolphin|octopus|jellyfish|reptile|insect|snake|spider|bug|worm|butterfly|eagle|hawk|owl|man|woman|child|kid|baby|family|crowd|portrait|selfie/i;
  const filtered = body.photos.filter(
    (item: any) => !blacklist.test((item.alt || "") + " " + (item.photographer || "").toLowerCase()),
  );

  const isMobile = window.innerWidth < 768;
  return filtered.map((item: any) => ({
    src: isMobile ? (item.src.large || item.src.original) : item.src.original,
    source: "Pexels",
    sourceLink: "https://pexels.com",
    credit: {
      imageLink: item.url,
      location: undefined,
      userName: item.photographer,
      userLink: item.photographer_url,
    },
  }));
};

// ---- Pixabay ----

const fetchPixabay = async (config: Config): Promise<Image[]> => {
  const query = config.search || pickQuery(PIXABAY_QUERIES);
  const url = `https://pixabay.com/api/?key=${PIXABAY_KEY}&q=${encodeURIComponent(query)}&per_page=40&orientation=horizontal&safesearch=true&image_type=photo&min_width=1920&order=latest&category=nature`;
  const res = await fetch(url, { cache: "no-cache" });
  const body = await res.json();
  console.log("[Nature Wallpaper] Pixabay response:", res.status, "hits:", body.hits?.length, "total:", body.totalHits);

  if (!body.hits) return [];

  const blacklist = /people|face|logo|brand|building|architecture|text|city|street|car|vehicle|animal|bird|dog|cat|pet|horse|deer|bear|wolf|fox|lion|tiger|elephant|cow|sheep|goat|pig|rabbit|squirrel|monkey|ape|fish|whale|shark|dolphin|octopus|jellyfish|reptile|insect|snake|spider|bug|worm|butterfly|eagle|hawk|owl/i;
  const filtered = body.hits.filter(
    (item: any) => !blacklist.test(item.tags || ""),
  );

  const isMobile = window.innerWidth < 768;
  return filtered.map((item: any) => ({
    src: isMobile ? (item.webformatURL || item.largeImageURL) : item.largeImageURL,
    source: "Pixabay",
    sourceLink: "https://pixabay.com",
    credit: {
      imageLink: item.pageURL,
      location: undefined,
      userName: item.user,
      userLink: `https://pixabay.com/users/${item.user}-${item.user_id}/`,
    },
  }));
};

// ---- NASA / Hubble ----

const fetchNasa = async (_config: Config): Promise<Image[]> => {
  try {
    const queries = ["nebula", "milky way"];
    const q = queries[dailySeed % queries.length];
    const page = (dailySeed % 10) + 1;
    const url = `https://images-api.nasa.gov/search?q=${encodeURIComponent(q)}&media_type=image&year_start=2010&page=${page}`;
    const res = await fetch(url, { cache: "no-cache" });
    const body = await res.json();

    const items = body?.collection?.items;
    if (!items?.length) return [];

    const shuffled = [...items].sort(() => Math.random() - 0.5).slice(0, 10);

    return shuffled
      .map((item: any) => {
        const data = item.data?.[0] || {};
        const href = item.links?.[0]?.href || "";
        const title = (data.title || "").toLowerCase();
        const desc = (data.description || "").toLowerCase();
        const text = title + " " + desc;
        if (/diagram|illustration|graph|chart|map|labeled|annotated|overlay|composite|processed|enhanced|artist.*concept|text|label|explanation|caption|infographic/i.test(text)) return null;
        return {
          src: href,
          source: "NASA",
          sourceLink: "https://images.nasa.gov",
          credit: {
            imageLink: href,
            location: undefined,
            userName: data.secondary_creator || data.center || "NASA",
            userLink: "https://images.nasa.gov",
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null && !!item.src);
  } catch {
    return [];
  }
};

// ---- Dispatch ----

const SOURCES: Array<{ key: ImageSource; fetch: (c: Config) => Promise<Image[]> }> = [
  { key: "unsplash", fetch: fetchUnsplash },
  { key: "pexels", fetch: fetchPexels },
  { key: "pixabay", fetch: fetchPixabay },
  { key: "nasa", fetch: fetchNasa },
];

const getAvailableSources = () => {
  return SOURCES.filter((s) => {
    if (s.key === "pexels" && !PEXELS_KEY) return false;
    if (s.key === "pixabay" && !PIXABAY_KEY) return false;
    return true;
  });
};

// ==============================
//  Persistent Wallpaper Pool
// ==============================

const POOL_KEY = "lumina-wallpaper-pool";
const URLS_KEY = "lumina-wallpaper-urls";
const CURSOR_KEY = "lumina-wallpaper-cursor";
const DAY_KEY = "lumina-wallpaper-day";
const MAX_POOL = 200;

interface PoolImage {
  src: string;
  source: string;
  sourceLink: string;
  credit: {
    imageLink: string;
    location?: string;
    userName: string;
    userLink: string;
  };
}

let _poolCache: PoolImage[] | null = null;
let _urlSetCache: Set<string> | null = null;

function getPool(): PoolImage[] {
  if (_poolCache) return _poolCache;
  try {
    const raw = localStorage.getItem(POOL_KEY);
    _poolCache = raw ? JSON.parse(raw) : [];
  } catch {
    _poolCache = [];
  }
  return _poolCache!;
}

function savePool(pool: PoolImage[]) {
  _poolCache = pool.slice(-MAX_POOL);
  try {
    localStorage.setItem(POOL_KEY, JSON.stringify(_poolCache));
  } catch {}
}

function getUrlSet(): Set<string> {
  if (_urlSetCache) return _urlSetCache;
  try {
    const raw = localStorage.getItem(URLS_KEY);
    _urlSetCache = new Set(raw ? JSON.parse(raw) : []);
  } catch {
    _urlSetCache = new Set();
  }
  return _urlSetCache!;
}

function saveUrlSet(set: Set<string>) {
  _urlSetCache = set;
  try {
    localStorage.setItem(URLS_KEY, JSON.stringify([...set]));
  } catch {}
}

export function getPoolCursor(): number {
  try {
    const v = localStorage.getItem(CURSOR_KEY);
    const cursor = v ? parseInt(v, 10) : 0;
    return cursor;
  } catch {
    return 0;
  }
}

export function clearPoolData() {
  localStorage.removeItem(POOL_KEY);
  localStorage.removeItem(URLS_KEY);
  localStorage.removeItem(CURSOR_KEY);
  localStorage.removeItem(DAY_KEY);
  _poolCache = null;
  _urlSetCache = null;
}

// Clear old pool data from previous versions on first load
(function migrateOldData() {
  try {
    const version = localStorage.getItem("lumina-pool-version");
    if (version !== "3") {
      clearPoolData();
      localStorage.setItem("lumina-pool-version", "3");
    }
  } catch {}
})();

export function savePoolCursor(cursor: number) {
  try {
    localStorage.setItem(CURSOR_KEY, String(cursor));
  } catch {}
}

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function shouldRefreshToday(): boolean {
  try {
    return localStorage.getItem(DAY_KEY) !== getToday();
  } catch {
    return true;
  }
}

function markToday() {
  try {
    localStorage.setItem(DAY_KEY, getToday());
  } catch {}
}

/**
 * Merge new images into the persistent pool, skipping duplicates.
 * Called once per day to refresh the pool with fresh content.
 */
export async function refreshPool(config: Config): Promise<boolean> {
  const available = getAvailableSources();
  const pool = getPool();
  const urlSet = getUrlSet();

  // Fetch from all available sources
  const results = await Promise.all(
    available.map(async (src) => {
      try {
        const imgs = await src.fetch(config);
        console.log(`[Wallpaper Pool] ${src.key}: ${imgs.length} images`);
        return imgs.slice(0, src.key === "nasa" ? 5 : 35);
      } catch {
        return [] as Image[];
      }
    }),
  );

  let added = 0;
  const allImages = results.flat().sort(() => Math.random() - 0.5);

  for (const img of allImages) {
    if (urlSet.has(img.src)) continue;
    urlSet.add(img.src);
    pool.push({
      src: img.src,
      source: img.source,
      sourceLink: img.sourceLink,
      credit: {
        imageLink: img.credit.imageLink,
        location: img.credit.location,
        userName: img.credit.userName,
        userLink: img.credit.userLink,
      },
    });
    added++;
  }

  // Shuffle the pool so new images are mixed in
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  savePool(pool);
  saveUrlSet(urlSet);
  markToday();
  console.log(`[Wallpaper Pool] Added ${added} new images. Pool size: ${pool.length}`);
  return added > 0;
}

/**
 * Ensure pool has at least some images (bootstrap).
 */
export async function ensurePool(config: Config): Promise<void> {
  const pool = getPool();
  if (pool.length === 0 || shouldRefreshToday()) {
    await refreshPool(config);
  }
}

/**
 * Get image at a specific cursor position from the pool.
 */
export function getPoolImage(index: number): PoolImage | null {
  const pool = getPool();
  if (index < 0 || index >= pool.length) return null;
  return pool[index];
}

export function getPoolSize(): number {
  return getPool().length;
}

/**
 * Remove the image at the given index from the pool.
 * Also removes its src from the dedup set so it can be re-fetched later.
 * Adjusts the cursor if it now points past the end.
 */
export function removePoolImage(index: number): void {
  const pool = getPool();
  if (index < 0 || index >= pool.length) return;

  const [removed] = pool.splice(index, 1);

  // Allow this image to be re-fetched in a future daily refresh
  const urlSet = getUrlSet();
  urlSet.delete(removed.src);

  savePool(pool);
  saveUrlSet(urlSet);

  // If the cursor now points past the end, wrap it
  const cursor = getPoolCursor();
  if (pool.length === 0) {
    savePoolCursor(0);
  } else if (cursor >= pool.length) {
    savePoolCursor(cursor % pool.length);
  }
}

/**
 * Build image link from raw
 */
export const buildLink = (src: string): string => {
  const url = new URL(src);
  url.searchParams.set("q", "85");
  url.searchParams.set(
    "w",
    String(calculateWidth(window.innerWidth, window.devicePixelRatio)),
  );
  return String(url);
};

export function calculateWidth(screenWidth = 1920, pixelRatio = 1): number {
  screenWidth = screenWidth * pixelRatio;
  screenWidth = Math.max(screenWidth, 1920);
  screenWidth = Math.min(screenWidth, 3840);
  screenWidth = Math.ceil(screenWidth / 240) * 240;
  return screenWidth;
}